/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled, { css, keyframes } from "styled-components";
import Icon from "./ui/Icon";
import { useTranslate } from "../hooks/useTranslate";

const spin = keyframes`
  100% {
    transform: rotate(1turn);
  }
`;

const appear = keyframes`
  from {
    opacity: 0;
  }
`;

const reducedMotionPulse = keyframes`
  50% {
    opacity: 0.4;
  }
`;

// Page loads that finish within 300ms never show the spinner.
const LoadingIndicatorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ $page }) =>
    $page &&
    css`
      min-height: 15rem;
      animation: ${appear} 0.2s ease-out 0.3s backwards;
    `}
`;

const LoadingIndicatorSpinner = styled((props) => <Icon.LoadingIndicator {...props} />)`
  color: rgba(${({ theme }) => theme.colors.border.rgba});
  stroke: rgba(${({ theme }) => theme.colors.accent.rgba});
  animation: ${spin} 0.75s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    && {
      animation: ${reducedMotionPulse} 1.5s ease-in-out infinite !important;
    }
  }
`;

const sizeStyles = {
  xl: Icon.SIZE.XL,
  lg: Icon.SIZE.LG,
  md: Icon.SIZE.MD,
  sm: Icon.SIZE.SM,
  xs: Icon.SIZE.XS,
};

const LoadingIndicator = ({ size = "xl", page = false, ...props }) => {
  const { _t } = useTranslate();

  return (
    <LoadingIndicatorWrapper $page={page} role="status" aria-label={_t("Loading")}>
      <LoadingIndicatorSpinner size={sizeStyles[size]} aria-hidden="true" {...props} />
    </LoadingIndicatorWrapper>
  );
};

export default LoadingIndicator;
